<template>
  <div class="dashboard-page">
    <el-card class="welcome-card" shadow="never">
      <h2>欢迎 {{ account }} 登录学途无忧网！</h2>
      <p class="welcome-desc">请从左侧菜单选择功能模块开始操作</p>
    </el-card>
    <div
      v-if="showAnnouncementBar"
      class="announcement-bar"
      :style="announcementStyle"
    >
      <i
        class="el-icon-date announcement-date-icon"
        :style="{ fontSize: `${announcementFontSize}px` }"
      />
      <div
        class="announcement-marquee"
        :class="{ 'announcement-marquee--scroll': announcementScroll }"
      >
        <div
          class="announcement-marquee__inner"
          :style="announcementScroll ? { animationDuration: `${announcementScrollSpeed}s` } : {}"
        >
          {{ announcementText }}
        </div>
      </div>
    </div>

    <div class="stat-card-group">
      <div v-for="item in displayStatCards" :key="item.title" class="stat-card-cell">
        <el-card class="stat-card" shadow="hover" @click.native="goToPage(item.route)">
          <div class="stat-content">
            <div class="stat-icon" :style="{ backgroundColor: item.color }">
              <i :class="item.icon"></i>
            </div>
            <div class="stat-info">
              <div class="stat-value">{{ item.value }}</div>
              <div class="stat-title">{{ item.title }}</div>
            </div>
          </div>
        </el-card>
      </div>
    </div>
    <div v-if="showTodoSection" class="todo-section">
      <div class="todo-title">我的待办</div>
      <div class="todo-card-group">
        <el-card
          v-if="canShowWithdrawAudit"
          class="todo-card"
          shadow="hover"
          @mouseenter.native="onTodoHover('withdraw')"
          @mouseleave.native="onTodoLeave"
          @click.native="onGotoWithdrawAudit"
        >
          <el-badge
            class="todo-card__badge-wrap"
            :value="withdrawPendingBadgeText"
            :hidden="withdrawPendingCount <= 0"
            type="danger"
          />
          <div class="todo-card__inner">
            <img class="todo-card__icon" :src="withdrawAuditIcon()" alt="提现审核" />
            <span class="todo-card__text">提现审核</span>
          </div>
        </el-card>
        <el-card
          v-if="canShowRealNameAudit"
          class="todo-card"
          shadow="hover"
          @mouseenter.native="onTodoHover('realName')"
          @mouseleave.native="onTodoLeave"
          @click.native="onGotoRealNameAudit"
        >
          <el-badge
            class="todo-card__badge-wrap"
            :value="realNamePendingBadgeText"
            :hidden="realNamePendingCount <= 0"
            type="danger"
          />
          <div class="todo-card__inner">
            <img class="todo-card__icon" :src="realNameAuditIcon()" alt="实名审核" />
            <span class="todo-card__text">实名审核</span>
          </div>
        </el-card>
      </div>
    </div>

  </div>
</template>

<script>
import api from '../services/api';
import { getUserRole, getRoleCode, getAllowedMenus, getAccount, getToken, decodeJwtPayload } from '@/utils/sessionAuth';
import withdrawIcon64 from '@/static/iconImage/tixianshenhe-icon-64.png';
import withdrawIcon128 from '@/static/iconImage/tixianshenhe-icon-128.png';
import realNameIcon64 from '@/static/iconImage/shimingshenhe-icon-64.png';
import realNameIcon128 from '@/static/iconImage/shimingshenhe-icon-128.png';

export default {
  name: 'Dashboard',
  data() {
    return {
      announcementText: '',
      announcementColor: 'rgba(255,140,0,1)',
      announcementFontSize: 22,
      announcementScrollSpeed: 26,
      announcementScroll: true,
      announcementPublished: false,
      announcementViewerRoles: null,
      hoveredTodo: '',
      withdrawPendingCount: 0,
      realNamePendingCount: 0,
      pendingCountTimer: null,
      statCards: [
        { title: '机构总数', value: 0, icon: 'el-icon-office-building', color: '#409EFF', route: '/org' },
        { title: '会员总数', value: 0, icon: 'el-icon-user', color: '#67C23A', route: '/staff' },
        { title: '学生总数', value: 0, icon: 'el-icon-s-custom', color: '#E6A23C', route: '/student' },
        {
          title: '推广账户',
          value: 0,
          icon: 'el-icon-s-marketing',
          color: '#13CE66',
          route: '/roi'
        },
        { title: '字典总数', value: 0, icon: 'el-icon-notebook-2', color: '#9B59B6', route: '/dict' }
      ]
    };
  },
  computed: {
    account() {
      const stored = getAccount();
      if (stored && String(stored).trim()) return String(stored).trim();
      const payload = decodeJwtPayload(getToken());
      if (payload) {
        const nick = payload.nickname != null ? String(payload.nickname).trim() : '';
        const phone = payload.phone != null ? String(payload.phone).trim() : '';
        if (nick) return nick;
        if (phone) return phone;
      }
      return '用户';
    },
    userRole() {
      return getUserRole() || '';
    },
    announcementStyle() {
      return {
        color: this.announcementColor,
        fontSize: `${this.announcementFontSize}px`
      };
    },
    showAnnouncementBar() {
      if (!this.announcementPublished) return false;
      if (!this.announcementText || !String(this.announcementText).trim()) return false;

      const roles = this.announcementViewerRoles;
      // 后端返回 null 表示未显式配置：默认全员可见
      if (roles == null) return true;
      if (!Array.isArray(roles)) return true;
      if (roles.length === 0) return false;
      if (!this.userRole) return false;
      return roles.includes(this.userRole);
    },
    normalizedRoleCode() {
      const raw = (getRoleCode() || getUserRole() || '').trim();
      return raw.toUpperCase();
    },
    normalizedUserRole() {
      return String(getUserRole() || '').trim().toUpperCase();
    },
    allowedMenus() {
      const arr = getAllowedMenus();
      return Array.isArray(arr) ? arr.map((x) => String(x || '').trim()) : [];
    },
    showTodoSection() {
      return this.canShowWithdrawAudit || this.canShowRealNameAudit;
    },
    canShowWithdrawAudit() {
      const hasWithdrawMenu = this.allowedMenus.includes('roi:audit');
      const roleCode = this.normalizedRoleCode;
      const userRole = this.normalizedUserRole;
      const account = String(this.account || '').trim().toUpperCase();
      const hitExact = ['ADMIN', 'SYSTEM_ADMIN', 'ACCOUNTANT', 'ACCOUNTING', '核算人员'].includes(roleCode)
        || ['ADMIN', 'SYSTEM_ADMIN', 'ACCOUNTANT', 'ACCOUNTING', '核算人员'].includes(userRole);
      const hitKeyword = roleCode.includes('核算') || userRole.includes('核算')
        || roleCode.includes('ACCOUNT') || userRole.includes('ACCOUNT')
        || roleCode.includes('FINANCE') || userRole.includes('FINANCE')
        || account.includes('核算') || account.includes('ACCOUNT') || account.includes('FINANCE');
      return hasWithdrawMenu || hitExact || hitKeyword;
    },
    canShowRealNameAudit() {
      const hasRealNameMenu = this.allowedMenus.includes('real-name-review');
      const roleCode = this.normalizedRoleCode;
      const userRole = this.normalizedUserRole;
      const account = String(this.account || '').trim().toUpperCase();
      const hitExact = ['ADMIN', 'SYSTEM_ADMIN', 'OPERATOR', 'OPERATION', '运营人员'].includes(roleCode)
        || ['ADMIN', 'SYSTEM_ADMIN', 'OPERATOR', 'OPERATION', '运营人员'].includes(userRole);
      const hitKeyword = roleCode.includes('运营') || userRole.includes('运营')
        || roleCode.includes('OPERATOR') || userRole.includes('OPERATOR')
        || roleCode.includes('OPERATION') || userRole.includes('OPERATION')
        || roleCode.includes('OPERATE') || userRole.includes('OPERATE')
        || account.includes('运营') || account.includes('OPERATOR') || account.includes('OPERATE');
      return hasRealNameMenu || hitExact || hitKeyword;
    },
    realNamePendingBadgeText() {
      const n = Number(this.realNamePendingCount || 0);
      if (n > 99) return '99+';
      return String(n);
    },
    withdrawPendingBadgeText() {
      const n = Number(this.withdrawPendingCount || 0);
      if (n > 99) return '99+';
      return String(n);
    },
    /** 与 sessionAuth 中「超级管理员/系统管理员」判断一致；字典总数统计仅此类角色可见 */
    isAdminOrSystemAdmin() {
      const rc = (getRoleCode() || '').trim();
      const ur = (getUserRole() || '').trim();
      const acc = (getAccount() || '').trim().toLowerCase();
      if (rc === 'SYSTEM_ADMIN' || ur === 'SYSTEM_ADMIN') return true;
      if (rc === 'admin' || ur === 'admin') return true;
      if (acc === 'admin') return true;
      if (ur.includes('系统管理员') || rc.includes('系统管理员')) return true;
      return false;
    },
    /** 商务人员（roleCode BUSINESS 或角色名含「商务」）：与 ElectronicMap/机构商务页签 约定一致 */
    isBusinessStaff() {
      if (this.normalizedRoleCode === 'BUSINESS' || this.normalizedUserRole === 'BUSINESS') {
        return true;
      }
      const raw = String(getUserRole() || getRoleCode() || '').trim();
      if (raw.includes('商务')) return true;
      return false;
    },
    /** 首页统计按钮：商务仅「机构总数」；「字典总数」仅 admin/系统管理员 */
    displayStatCards() {
      if (this.isBusinessStaff) {
        return this.statCards.filter((c) => c.title === '机构总数');
      }
      if (!this.isAdminOrSystemAdmin) {
        return this.statCards.filter((c) => c.title !== '字典总数');
      }
      return this.statCards;
    }
  },
  mounted() {
    this.loadAnnouncementConfig();
    this.loadStats();
    this.loadWithdrawPendingCount();
    this.loadRealNamePendingCount();
    this.startPendingCountPolling();
    this.$root.$on('announcement-config-updated', this.loadAnnouncementConfig);
  },
  beforeDestroy() {
    this.stopPendingCountPolling();
    this.$root.$off('announcement-config-updated', this.loadAnnouncementConfig);
  },
  methods: {
    goToPage(route) {
      if (route) {
        this.$router.push(route);
      }
    },
    async loadAnnouncementConfig() {
      try {
        const res = await api.post('/system/announcement-config/query', {});
        if (res.data && res.data.success) {
          const c = res.data.data || {};
          this.announcementText = c.announcement || '';
          this.announcementColor = c.announcementColor || 'rgba(255,140,0,1)';
          this.announcementFontSize = Number.isFinite(c.announcementFontSize) ? c.announcementFontSize : 22;
          this.announcementScrollSpeed = Number.isFinite(c.announcementScrollSpeed) ? c.announcementScrollSpeed : 26;
          this.announcementScroll = !!c.announcementScroll;
          this.announcementPublished = !!c.announcementPublished;
          this.announcementViewerRoles = c.announcementViewerRoles;
        }
      } catch (e) {
        // ignore
      }
    },
    async loadStats() {
      try {
        if (this.isBusinessStaff) {
          const orgRes = await api.post('/org/list', { pageSize: 1 });
          const orgCard = this.statCards.find((c) => c.title === '机构总数');
          if (orgCard) orgCard.value = orgRes.data.data?.total || 0;
          return;
        }
        if (this.isAdminOrSystemAdmin) {
          const [orgRes, staffRes, studentRes, dictRes, promoRes] = await Promise.all([
            api.post('/org/list', { pageSize: 1 }),
            api.post('/staff/list', { pageSize: 1 }),
            api.post('/student/list', { pageSize: 1 }),
            api.post('/dict/list', { pageSize: 1 }),
            api.post('/promotion-account/stats', {})
          ]);
          this.statCards[0].value = orgRes.data.data?.total || 0;
          this.statCards[1].value = staffRes.data.data?.total || 0;
          this.statCards[2].value = studentRes.data.data?.total || 0;
          const promo = promoRes.data.data;
          const promoCard = this.statCards.find((c) => c.title === '推广账户');
          if (promoCard && promo) promoCard.value = promo.total ?? 0;
          const dictCard = this.statCards.find((c) => c.title === '字典总数');
          if (dictCard) dictCard.value = dictRes.data.data?.total || 0;
          return;
        }
        const [orgRes, staffRes, studentRes, promoRes] = await Promise.all([
          api.post('/org/list', { pageSize: 1 }),
          api.post('/staff/list', { pageSize: 1 }),
          api.post('/student/list', { pageSize: 1 }),
          api.post('/promotion-account/stats', {})
        ]);
        this.statCards[0].value = orgRes.data.data?.total || 0;
        this.statCards[1].value = staffRes.data.data?.total || 0;
        this.statCards[2].value = studentRes.data.data?.total || 0;
        const promo = promoRes.data.data;
        const promoCard = this.statCards.find((c) => c.title === '推广账户');
        if (promoCard && promo) promoCard.value = promo.total ?? 0;
      } catch (e) {
        console.error('加载统计数据失败', e);
      }
    },
    async loadRealNamePendingCount() {
      try {
        if (!this.canShowRealNameAudit) {
          this.realNamePendingCount = 0;
          return;
        }
        const res = await api.post('/staff/real-name/review/list', {
          status: 'PENDING',
          page: 1,
          pageSize: 1
        });
        const total = Number(res && res.data && res.data.data ? res.data.data.total : 0);
        this.realNamePendingCount = Number.isFinite(total) && total > 0 ? total : 0;
      } catch (e) {
        this.realNamePendingCount = 0;
      }
    },
    async loadWithdrawPendingCount() {
      try {
        if (!this.canShowWithdrawAudit) {
          this.withdrawPendingCount = 0;
          return;
        }
        const res = await api.post('/staff/withdrawal/review/list', {
          status: 'PENDING',
          page: 1,
          pageSize: 1
        });
        const total = Number(res && res.data && res.data.data ? res.data.data.total : 0);
        this.withdrawPendingCount = Number.isFinite(total) && total > 0 ? total : 0;
      } catch (e) {
        this.withdrawPendingCount = 0;
      }
    },
    startPendingCountPolling() {
      this.stopPendingCountPolling();
      this.pendingCountTimer = setInterval(() => {
        this.loadWithdrawPendingCount();
        this.loadRealNamePendingCount();
      }, 30000);
    },
    stopPendingCountPolling() {
      if (this.pendingCountTimer) {
        clearInterval(this.pendingCountTimer);
        this.pendingCountTimer = null;
      }
    },
    onGotoWithdrawAudit() {
      this.$router.push('/withdraw-audit');
    },
    onGotoRealNameAudit() {
      this.$router.push('/real-name-review');
    },
    onTodoHover(key) {
      this.hoveredTodo = key;
    },
    onTodoLeave() {
      this.hoveredTodo = '';
    },
    withdrawAuditIcon() {
      return this.hoveredTodo === 'withdraw' ? withdrawIcon128 : withdrawIcon64;
    },
    realNameAuditIcon() {
      return this.hoveredTodo === 'realName' ? realNameIcon128 : realNameIcon64;
    }
  }
};
</script>

<style scoped>
.dashboard-page {
  padding: 0;
}

.announcement-bar {
  margin: 20px 0 8px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  box-shadow: 0 1px 10px rgba(0, 0, 0, 0.04);
  display: flex;
  align-items: center;
}

.announcement-date-icon {
  flex-shrink: 0;
  margin-right: 10px;
  /* 图标颜色固定（不受公告配置影响） */
  color: #409EFF;
}

.announcement-marquee {
  flex: 1;
  min-width: 0; /* 关键：避免 marquee 因内容宽度而收缩，导致表现异常 */
  overflow: hidden;
  white-space: nowrap;
}

.announcement-marquee__inner {
  display: inline-block;
  white-space: nowrap;
}

.announcement-marquee--scroll .announcement-marquee__inner {
  /* 关键：padding-left = 100%（相对容器宽度）让文本初始时完全出现在右侧遮罩之外 */
  padding-left: 100%;
  animation-name: dashboard-announcement-marquee-rtl;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-duration: 26s;
}

.announcement-marquee--scroll:hover .announcement-marquee__inner {
  animation-play-state: paused;
}

/* 我的待办 */
.todo-section {
  margin: 12px 0 14px;
}

.todo-title {
  font-size: 18px;
  font-weight: 700;
  color: #303133;
  margin-bottom: 10px;
}

.todo-card-group {
  padding: 20px;
  display: flex;
  gap: 20px;
  justify-items: start;
}

.todo-card {
  cursor: pointer;
  border-radius: 10px;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  max-width: 307px;
  width: 100%;
  position: relative;
}

.todo-card >>> .el-card__body {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.todo-card:hover {
  transform: translateY(-2px);
  transition: all 0.2s ease;
}

.todo-card__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 100%;
  position: relative;
}

.todo-card__badge-wrap {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 3;
}
.todo-card__badge-wrap >>> .el-badge__content {
  font-weight: 600;
}

.todo-card__icon {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: none;
}

.todo-card__text {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

@keyframes dashboard-announcement-marquee-rtl {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-100%);
  }
}

/* 同一行内等分宽度，随容器变窄自动换行，每行内各格仍等宽 */
.stat-card-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 20px;
  margin-top: 20px;
  margin-bottom: 4px;
}

.stat-card-cell {
  min-width: 0;
}

.stat-card {
  width: 100%;
  margin-bottom: 0;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
}

.stat-content {
  display: flex;
  align-items: center;
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  background: linear-gradient(135deg, var(--icon-color) 0%, var(--icon-color-light) 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}

.stat-icon i {
  font-size: 26px;
  color: #fff;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 26px;
  font-weight: 700;
  color: #303133;
  line-height: 1.2;
}

.stat-title {
  font-size: 13px;
  color: #909399;
  margin-top: 4px;
  font-weight: 500;
}

.welcome-card {
  margin-top: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.welcome-card h2 {
  margin: 0 0 12px;
  color: #fff;
  font-size: 22px;
  font-weight: 600;
}

.welcome-desc {
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  font-size: 14px;
}
</style>
